import 'react-quill-new/dist/quill.core.css';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { Button, Tooltip } from 'antd';
import { ShoppingCartOutlined, CheckOutlined, StopOutlined } from '@ant-design/icons';
import { FaMapMarkerAlt } from 'react-icons/fa';
import './EventDetailPage.css';
import { doc, getDoc, getDocs, collection } from 'firebase/firestore';
import { Table, message } from 'antd';
import { useCart } from '../CartContext';
import Footer from './Footer';

function EventDetailPage() {
  const { eventSlug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addedToCart, setAddedToCart] = useState(null);
  const { addToCart, cart, updateQuantity } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const eventsQuery = await getDocs(collection(db, 'events'));
        const matchingEvent = eventsQuery.docs.find((doc) => {
  if (doc.data().archived) return false;
  const eventName = doc
    .data()
    .name.toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/(^_+|_+$)/g, '');
  return eventName === eventSlug;
});

        if (matchingEvent) {
          setEvent({ id: matchingEvent.id, ...matchingEvent.data() });
        }
      } catch (error) {
        console.error('Error fetching event:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventSlug]);

  const handleAddToCart = (ticket) => {
    addToCart({
      event: event.name,
      ticketType: ticket.type,
      price: ticket.price,
      maxQty: ticket.qty ? parseInt(ticket.qty) - (ticket.sold ?? 0) : null,
    });
    setAddedToCart(ticket.type);
    message.success('Added to cart successfully!');
  };

  const isSoldOut = (t) => t.qty && (t.sold ?? 0) >= parseInt(t.qty);

  const isAtCartLimit = (t) => {
    if (!t.qty) return false;
    const remaining = parseInt(t.qty) - (t.sold ?? 0);
    const inCart = cart.find(
      (item) => item.event === event?.name && item.ticketType === t.type
    );
    return (inCart?.quantity || 0) >= remaining;
  };

  const getCartQty = (ticket) => {
    const item = cart.find(
      (i) => i.event === event?.name && i.ticketType === ticket.type
    );
    return item?.quantity || 0;
  };

  const columns = [
    {
      title: 'Ticket Type',
      dataIndex: 'type',
      key: 'type',
      render: (type, record) => (
        <div>
          <div style={{ fontWeight: 'bold', color: 'white' }}>{type}</div>
          {isSoldOut(record) ? (
            <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px', fontWeight: 'bold' }}>🚫 Sold Out</div>
          ) : getCartQty(record) > 0 ? (
            <div style={{ color: '#28a745', fontSize: '12px', marginTop: '4px', fontWeight: 'bold' }}>✅ In cart</div>
          ) : record.qty ? (
            <div style={{ color: '#28a745', fontSize: '12px', marginTop: '4px', fontWeight: 'bold' }}>
              ✅ {parseInt(record.qty) - (record.sold ?? 0)} left
            </div>
          ) : (
            <div style={{ color: '#28a745', fontSize: '12px', marginTop: '4px' }}>✅ Available</div>
          )}
        </div>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price) => `$${parseFloat(price || 0).toFixed(2)}`,
    },
    {
      title: 'Add to Cart',
      key: 'action',
      render: (_, record) => {
        const qty = getCartQty(record);
        const maxQty = record.qty ? parseInt(record.qty) - (record.sold ?? 0) : 99;
        const inCartItem = cart.find(
          (i) => i.event === event?.name && i.ticketType === record.type
        );

        if (isSoldOut(record)) {
          return (
            <button disabled style={{
              padding: '6px 12px', borderRadius: '6px',
              background: '#555', border: 'none', color: '#aaa',
              fontSize: '13px', cursor: 'not-allowed'
            }}>
              Sold Out
            </button>
          );
        }

        if (qty === 0) {
          return (
            <button
              onClick={() => handleAddToCart(record)}
              style={{
                padding: '6px 14px', borderRadius: '6px',
                background: '#2563eb', border: 'none', color: 'white',
                fontSize: '13px', fontWeight: 'bold', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px',
                transition: 'background 0.2s ease'
              }}
            >
              🛒 Add to Cart
            </button>
          );
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              onClick={() => {
                if (qty - 1 === 0) updateQuantity(inCartItem, 0);
                else updateQuantity(inCartItem, qty - 1);
              }}
              style={{
                width: '28px', height: '28px', borderRadius: '6px',
                border: '1px solid #444', background: '#333',
                color: 'white', cursor: 'pointer', fontSize: '16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >−</button>
            <span style={{
              color: '#333', fontWeight: 'bold',
              minWidth: '20px', textAlign: 'center',
              background: 'white', padding: '2px 8px',
              borderRadius: '4px', fontSize: '13px',
              border: '1px solid #ddd'
            }}>{qty}</span>
            <button
              onClick={() => {
                if (qty < maxQty) updateQuantity(inCartItem, qty + 1);
              }}
              disabled={qty >= maxQty}
              style={{
                width: '28px', height: '28px', borderRadius: '6px',
                border: '1px solid #444',
                background: qty >= maxQty ? '#2a2a2a' : '#2563eb',
                color: 'white', cursor: qty >= maxQty ? 'not-allowed' : 'pointer',
                fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >+</button>
          </div>
        );
      },
    },
  ];

  if (loading) return (
    <div className="event-detail-loading">
      <div style={{ width: '40px', height: '40px', border: '4px solid #333', borderTopColor: '#007bff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
    </div>
  );

  if (!event) return (
    <div className="event-detail-not-found">
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: '48px', marginBottom: '16px' }}>🎟</p>
        <h2 style={{ color: 'white', marginBottom: '12px' }}>Event not found</h2>
        <button onClick={() => navigate('/')} style={{ padding: '12px 28px', background: '#007bff', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: 'bold' }}>
          Back to Home
        </button>
      </div>
    </div>
  );

  return (
    <div className="event-page-container page-enter">
      <div className="event-detail-container">
        <div className="event-detail-content" data-aos="fade-up">
          <div className="event-images-stack">
            {(event.images || [event.imageURL])
              .filter(Boolean)
              .map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Event image ${index + 1}`}
                  className="event-detail-image"
                />
              ))}
          </div>

          <h1 data-aos="fade-up" data-aos-delay="100">{event.name}</h1>

          {event.location && (
            <div className="event-detail-location">
              <FaMapMarkerAlt className="location-icon" />
              <span>{event.location}</span>
            </div>
          )}

          {event.datetime && (
            <div className="event-detail-datetime">📅 {event.datetime}</div>
          )}

          <div
  className="event-detail-description"
  dangerouslySetInnerHTML={{ __html: event.description.replace(/&nbsp;/g, ' ') }}
/>

          {event.tickets && (
            <Table
              dataSource={event.tickets}
              columns={columns}
              pagination={false}
              rowKey="type"
              style={{ marginTop: '24px' }}
            />
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default EventDetailPage;